import { javaFixed } from '../javaText.ts';

/**
 * Map geometry, ported from the desktop's `MapGeometry` (docs/PARITY-STEP-4-PLAN.md section 4):
 * points in map-image pixels, stored as "x,y;x,y;..." with whole numbers written whole and
 * others to two places; centroids, areas and lengths; and the point-in-polygon test.
 * Checked against `schema/fixtures/builtin-icons.json`'s "geometry".
 */

export interface MapPoint {
  readonly x: number;
  readonly y: number;
}

/** `MapGeometry.format`: whole numbers whole, others to two places as Java's `%.2f` writes them. */
export function formatNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : javaFixed(value, 2);
}

export function encodePoints(points: readonly MapPoint[]): string {
  return points.map((p) => `${formatNumber(p.x)},${formatNumber(p.y)}`).join(';');
}

/** `MapGeometry.decode`: a malformed pair is skipped, not the whole outline. */
export function decodePoints(text: string | null | undefined): MapPoint[] {
  if (!text || text.trim() === '') return [];
  const points: MapPoint[] = [];
  for (const pair of text.split(';')) {
    const parts = pair.split(',');
    if (parts.length !== 2) continue;
    const [x, y] = parts.map((p) => javaDouble(p.trim()));
    if (x === null || y === null) continue;
    points.push({ x: x!, y: y! });
  }
  return points;
}

/** `Double.parseDouble`, or null where it would throw. */
function javaDouble(text: string): number | null {
  if (!/^[+-]?(NaN|Infinity|((\d+\.?\d*|\.\d+)([eE][+-]?\d+)?))[fFdD]?$/.test(text)) return null;
  return Number(text.replace(/[fFdD]$/, ''));
}

function signedArea(points: readonly MapPoint[]): number {
  let sum = 0;
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const a = points[i]!;
    const b = points[(i + 1) % n]!;
    sum += a.x * b.y - b.x * a.y;
  }
  return sum / 2;
}

export function area(points: readonly MapPoint[]): number {
  return Math.abs(signedArea(points));
}

export function pathLength(points: readonly MapPoint[]): number {
  let length = 0;
  for (let i = 1; i < points.length; i++) length += Math.hypot(points[i]!.x - points[i - 1]!.x, points[i]!.y - points[i - 1]!.y);
  return length;
}

/** Where a region's name sits: its area centroid, or the vertices' average for a shape with no area. */
export function centroid(points: readonly MapPoint[]): MapPoint {
  if (points.length === 0) return { x: 0, y: 0 };
  const a = signedArea(points);
  if (Math.abs(a) < 1e-9) {
    return { x: points.reduce((s, p) => s + p.x, 0) / points.length, y: points.reduce((s, p) => s + p.y, 0) / points.length };
  }
  let cx = 0;
  let cy = 0;
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const p = points[i]!;
    const q = points[(i + 1) % n]!;
    const cross = p.x * q.y - q.x * p.y;
    cx += (p.x + q.x) * cross;
    cy += (p.y + q.y) * cross;
  }
  return { x: cx / (6 * a), y: cy / (6 * a) };
}

/** Ray casting, as `MapGeometry.contains`. */
export function contains(polygon: readonly MapPoint[], point: MapPoint): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i]!;
    const b = polygon[j]!;
    if (a.y > point.y !== b.y > point.y && point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}
